import { formatCurrency, getCurrencySymbol } from '@angular/common';
import {
  TreadDepthSetting,
  VehicleBrakeMeasurement,
  VehicleInspectionReportHeader,
  VehicleInspectionTask,
} from '@quorum/models/xs-resource';

export const TaskStatus = {
  Green: 'green',
  Yellow: 'yellow',
  Red: 'red',
  Grey: 'grey',
} as const;
export type TaskStatus = typeof TaskStatus[keyof typeof TaskStatus];

export interface TaskRowViewModel {
  readonly taskId: number;
  readonly label: string;
  readonly status: TaskStatus;
  // Right-aligned readings (oil life, battery voltage), in print order.
  readonly measurements: readonly string[];
}

// A tread-depth task prints as a card rather than a row. Readings are already formatted; a value
// that was never recorded prints as NOT_RECORDED.
export interface TireCardViewModel {
  readonly taskId: number;
  readonly label: string;
  readonly status: TaskStatus;
  // Any oil-life / voltage readings the task also carries, printed after the tire readings.
  readonly measurements: readonly string[];
  readonly psiBefore: string;
  readonly psiSetTo: string;
  readonly treadDepth: string;
  readonly wearPattern: string;
}

export interface SectionViewModel {
  readonly categoryId: number;
  readonly heading: string;
  // Cards print as a two-up grid above the rows, in task order.
  readonly tireCards: readonly TireCardViewModel[];
  readonly rows: readonly TaskRowViewModel[];
}

export interface TreadLegendEntry {
  readonly status: TaskStatus;
  readonly label: string;
}

export interface TreadLegendViewModel {
  readonly entries: readonly TreadLegendEntry[];
}

export interface ReportHeaderViewModel {
  readonly title: string;
  readonly customerName: string;
  readonly advisorName: string;
  readonly date: string;
  readonly vehicle: string;
  readonly workOrderId: string;
  readonly tag: string;
  readonly vin: string;
}

// One quote case, in print order. Money is already formatted; text fields that were left blank
// carry QUOTE_FIELD_NOT_AVAILABLE and a missing expiry date EXPIRY_DATE_NOT_AVAILABLE.
export interface QuoteCaseViewModel {
  readonly caseNumber: string;
  readonly taskName: string;
  readonly labourHours: string;
  readonly expires: string;
  readonly employee: string;
  readonly concern: string;
  readonly cause: string;
  readonly correction: string;
  readonly labour: string;
  readonly parts: string;
  readonly additionalCharges: string;
  readonly tax: string;
  readonly total: string;
}

export interface QuoteDetailsViewModel {
  readonly cases: readonly QuoteCaseViewModel[];
  readonly total: string;
}

export interface InspectionReportViewModel {
  readonly header: ReportHeaderViewModel;
  // Null when the rooftop has no tread-depth thresholds; the grid then prints without a legend.
  readonly treadLegend: TreadLegendViewModel | null;
  readonly sections: readonly SectionViewModel[];
  // Null when the work order has no quote lines; the report then ends with the last section.
  readonly quote: QuoteDetailsViewModel | null;
}

// The fields of GET /v/2/vip/inspections/{workOrderId} the report reads. Structural on purpose:
// the full payload satisfies it, and so does a fixture, without depending on every column.
export interface RawInspectionTask {
  readonly taskId: number;
  readonly taskName: string | null;
  readonly greenBox: boolean | null;
  readonly yellowBox: boolean | null;
  readonly redBox: boolean | null;
  readonly greyBox: boolean | null;
  readonly displayOilLife?: boolean | null;
  readonly oilLife?: number | null;
  readonly displayVoltage?: boolean | null;
  readonly batteryVoltage?: number | null;
  readonly displayFluidFill?: boolean | null;
  readonly fluidFilled?: boolean | null;
  readonly displayTireSealantExpiration?: boolean | null;
  readonly vehicleSealantExpirationDate?: string | null;
  readonly displayBrakeMeasurement?: boolean | null;
  readonly brakeMeasurementId?: number | null;
  readonly displayTireTread?: boolean | null;
  readonly psiIn?: number | null;
  readonly psiOut?: number | null;
  readonly treadDepth?: number | null;
  readonly wearPattern?: string | null;
}

export interface RawInspectionCategory {
  readonly categoryId: number;
  readonly categoryName: string | null;
  readonly categorySequence: number | null;
  readonly tasks?: readonly RawInspectionTask[] | null;
}

// The server splits the static tire category's tasks across `tires` and `tireOther`, so one
// category can appear in up to three arrays; the builder re-merges them by categoryId.
export interface RawInspectionPayload {
  readonly name?: string | null;
  readonly categories?: readonly RawInspectionCategory[] | null;
  readonly tires?: readonly RawInspectionCategory[] | null;
  readonly tireOther?: readonly RawInspectionCategory[] | null;
}

// The quote lines of GET /v/2/vip/inspections/vcr-report/{workOrderId}/{quoteId}, as
// pGetVehicleInspectionQuoteDetail aliases them. The datetime crosses the wire as ISO text; a
// Date is accepted for callers holding the typed model.
export interface RawQuoteLine {
  readonly Case: number | null;
  readonly TaskName?: string | null;
  readonly Note?: string | null;
  readonly Cause?: string | null;
  readonly Recommendation?: string | null;
  readonly ExpirationDate?: Date | string | null;
  readonly RecommendedHours?: number | null;
  readonly Employee?: string | null;
  readonly labourSubTotal?: number | null;
  readonly partsSubTotal?: number | null;
  readonly additionalCharges?: number | null;
  readonly tax?: number | null;
  readonly CaseTotal?: number | null;
}

// The locale and currency the app's currency pipe would use, so the PDF prints money the way the
// inspection screen does.
export interface CurrencyFormat {
  readonly locale: string;
  readonly currencyCode: string;
}

export type TaskSequence = Pick<VehicleInspectionTask, 'id' | 'sequence'>;

export type BrakeMeasurementOption = Pick<VehicleBrakeMeasurement, 'id' | 'measurement'>;

export type TreadDepthThresholds = Pick<TreadDepthSetting, 'greenTreadDepth' | 'yellowTreadDepth' | 'redTreadDepth'>;

export interface InspectionReportSource {
  readonly inspection: RawInspectionPayload;
  readonly taskSequences: readonly TaskSequence[];
  readonly header: VehicleInspectionReportHeader | null;
  readonly brakeMeasurements: readonly BrakeMeasurementOption[];
  readonly treadDepthThresholds: TreadDepthThresholds | null;
  // Tasks the inspection screen hides (the rear tire positions the Dual Rear Tires toggle
  // replaces); they are left out of the report rather than printed as Not Inspected.
  readonly omittedTaskIds: readonly number[];
  readonly quoteLines: readonly RawQuoteLine[];
  readonly currency: CurrencyFormat;
}

export const DEFAULT_REPORT_TITLE = 'Vehicle Inspection';
export const QUOTE_FIELD_NOT_AVAILABLE = 'Not Available';
export const EXPIRY_DATE_NOT_AVAILABLE = 'N/A';
export const OIL_LIFE_NOT_AVAILABLE = 'N/A';
export const VOLTAGE_NOT_MEASURED = '--';
export const FLUID_FILLED_SUFFIX = 'Filled';
export const NOT_RECORDED = '--';
export const TREAD_DEPTH_UNIT = '/32';

// The database stores "no sealant date" as a date at or before this; the inspection screen nulls
// such a value the same way before showing the task.
const SEALANT_DATE_SENTINEL_LIMIT = Date.UTC(1901, 0, 1);
const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;

interface MergedCategory {
  readonly categoryId: number;
  readonly categoryName: string | null;
  readonly categorySequence: number | null;
  // Served in `tires` / `tireOther`: the category the server itself treats as the tire category.
  isTireCategory: boolean;
  readonly tasks: RawInspectionTask[];
}

export function collectCategoryIds(inspection: RawInspectionPayload): number[] {
  return mergeCategories(inspection).map((category) => category.categoryId);
}

export function buildInspectionReportViewModel(source: InspectionReportSource): InspectionReportViewModel {
  const sequenceByTaskId = toSequenceMap(source.taskSequences);
  const measurementById = toBrakeMeasurementMap(source.brakeMeasurements);
  const omittedTaskIds = new Set(source.omittedTaskIds);

  const sections = mergeCategories(source.inspection)
    .sort(compareCategories)
    .map((category): BuiltSection => {
      const tasks = category.tasks
        .filter((task) => !omittedTaskIds.has(task.taskId))
        .sort((a, b) => compareTasks(a, b, sequenceByTaskId));
      return {
        categoryId: category.categoryId,
        heading: category.categoryName ?? '',
        // A category whose every task was hidden on screen is not worth a heading.
        isEmptiedByOmission: category.tasks.length > 0 && tasks.length === 0,
        isTireCategory: category.isTireCategory,
        tireCards: tasks.filter(isTreadTask).map((task) => toTireCard(task, measurementById)),
        rows: tasks.filter((task) => !isTreadTask(task)).map((task) => toTaskRow(task, measurementById)),
      };
    })
    .filter((section) => !section.isEmptiedByOmission);

  return {
    header: buildHeader(source.header, source.inspection.name ?? null),
    treadLegend: buildTreadLegend(source.treadDepthThresholds),
    sections: groupTireSections(sections).map(({ categoryId, heading, tireCards, rows }) => ({
      categoryId,
      heading,
      tireCards,
      rows,
    })),
    quote: buildQuoteDetails(source.quoteLines, source.currency),
  };
}

// Cases print in case-number order, whichever order the quote stored them in, and the quote
// total is the sum of their case totals, as the vcr-report screen sums them.
export function buildQuoteDetails(
  quoteLines: readonly RawQuoteLine[],
  currency: CurrencyFormat
): QuoteDetailsViewModel | null {
  const lines = quoteLines.filter((line) => line != null);
  if (lines.length === 0) {
    return null;
  }

  const money = (value: number | null | undefined): string => formatMoney(value ?? 0, currency);
  const cases = lines
    .slice()
    .sort((a, b) => compareNullableNumbers(a.Case, b.Case))
    .map(
      (line): QuoteCaseViewModel => ({
        caseNumber: asText(line.Case),
        taskName: asText(line.TaskName),
        labourHours: String(line.RecommendedHours ?? 0),
        expires: toExpirationDate(line.ExpirationDate) ?? EXPIRY_DATE_NOT_AVAILABLE,
        employee: asText(line.Employee),
        concern: nonEmpty(line.Note) ?? QUOTE_FIELD_NOT_AVAILABLE,
        cause: nonEmpty(line.Cause) ?? QUOTE_FIELD_NOT_AVAILABLE,
        correction: nonEmpty(line.Recommendation) ?? QUOTE_FIELD_NOT_AVAILABLE,
        labour: money(line.labourSubTotal),
        parts: money(line.partsSubTotal),
        additionalCharges: money(line.additionalCharges),
        tax: money(line.tax),
        total: money(line.CaseTotal),
      })
    );

  return {
    cases,
    total: money(lines.reduce((sum, line) => sum + (line.CaseTotal ?? 0), 0)),
  };
}

// The same rendering as Angular's currency pipe with no arguments: the locale's symbol for the
// default currency code, so a Canadian and an American rooftop both print "$1,234.56".
function formatMoney(value: number, currency: CurrencyFormat): string {
  return formatCurrency(
    value,
    currency.locale,
    getCurrencySymbol(currency.currencyCode, 'wide', currency.locale),
    currency.currencyCode
  );
}

// Printed as MM.DD.YYYY, the format every HTML template used; null when there is no date.
export function toExpirationDate(value: Date | string | null | undefined): string | null {
  const date = parseIsoCalendarDate(value instanceof Date ? value.toISOString() : value);
  return date === null ? null : `${date.month}.${date.day}.${date.year}`;
}

interface BuiltSection extends SectionViewModel {
  readonly isEmptiedByOmission: boolean;
  readonly isTireCategory: boolean;
}

// The inspection screen renders every tread-bearing category with the Tires card, whatever its
// own sequence says (Dual Rear Tires is configured as a separate category that sorts elsewhere).
// The report does the same: the server's tire category keeps its place and leads, and every other
// section with tire cards follows it in sequence order, so the tire cards are read together.
function groupTireSections(sections: readonly BuiltSection[]): BuiltSection[] {
  const tireSections = sections.filter(hasTireCards);
  const anchor = tireSections.find((section) => section.isTireCategory) ?? tireSections[0];
  if (anchor === undefined) {
    return [...sections];
  }

  const rowSections = sections.filter((section) => !hasTireCards(section));
  const anchorIndex = sections.indexOf(anchor);
  const rowSectionsBeforeAnchor = rowSections.filter((section) => sections.indexOf(section) < anchorIndex);
  return [
    ...rowSectionsBeforeAnchor,
    anchor,
    ...tireSections.filter((section) => section !== anchor),
    ...rowSections.slice(rowSectionsBeforeAnchor.length),
  ];
}

function hasTireCards(section: SectionViewModel): boolean {
  return section.tireCards.length > 0;
}

// The trigger is the task's own flag, so dual rear, spare and dealer-added tire tasks get a card
// without the report knowing their names.
function isTreadTask(task: RawInspectionTask): boolean {
  return Boolean(task.displayTireTread);
}

// A tread task can carry the other widgets too (the inspection screen shows a sealant expiry or
// brake reading beside the tread inputs), so the card keeps the same label suffixes and
// measurements a row would print.
function toTireCard(task: RawInspectionTask, measurementById: ReadonlyMap<number, string>): TireCardViewModel {
  const status = toTaskStatus(task);
  return {
    taskId: task.taskId,
    label: buildTaskLabel(task, measurementById),
    status,
    measurements: buildMeasurements(task, status),
    psiBefore: toReading(task.psiIn),
    psiSetTo: toReading(task.psiOut),
    treadDepth:
      task.treadDepth != null && task.treadDepth !== 0 ? `${task.treadDepth}${TREAD_DEPTH_UNIT}` : NOT_RECORDED,
    wearPattern: asText(task.wearPattern),
  };
}

// 0 is the stored default for a reading that was never entered.
function toReading(value: number | null | undefined): string {
  return value != null && value !== 0 ? String(value) : NOT_RECORDED;
}

// The bands the four HTML templates print: green from its threshold up, yellow up to one below
// green, red from one below yellow down. The red threshold itself is not printed.
export function buildTreadLegend(thresholds: TreadDepthThresholds | null): TreadLegendViewModel | null {
  const green = thresholds?.greenTreadDepth;
  const yellow = thresholds?.yellowTreadDepth;
  if (green == null || yellow == null) {
    return null;
  }

  return {
    entries: [
      { status: TaskStatus.Green, label: `${green}${TREAD_DEPTH_UNIT} or greater` },
      { status: TaskStatus.Yellow, label: `${yellow}${TREAD_DEPTH_UNIT} to ${green - 1}${TREAD_DEPTH_UNIT}` },
      { status: TaskStatus.Red, label: `${yellow - 1}${TREAD_DEPTH_UNIT} or less` },
    ],
  };
}

function mergeCategories(inspection: RawInspectionPayload): MergedCategory[] {
  const merged = new Map<number, MergedCategory>();
  const seenTaskIds = new Set<number>();

  const groups: readonly { categories: readonly RawInspectionCategory[] | null | undefined; isTire: boolean }[] = [
    { categories: inspection.categories, isTire: false },
    { categories: inspection.tires, isTire: true },
    { categories: inspection.tireOther, isTire: true },
  ];

  groups.forEach(({ categories, isTire }) => {
    (categories ?? []).forEach((category) => {
      if (category == null) {
        return;
      }

      let target = merged.get(category.categoryId);
      if (!target) {
        target = {
          categoryId: category.categoryId,
          categoryName: category.categoryName,
          categorySequence: category.categorySequence,
          isTireCategory: false,
          tasks: [],
        };
        merged.set(category.categoryId, target);
      }
      target.isTireCategory = target.isTireCategory || isTire;

      (category.tasks ?? []).forEach((task) => {
        if (task == null || seenTaskIds.has(task.taskId)) {
          return;
        }
        seenTaskIds.add(task.taskId);
        target.tasks.push(task);
      });
    });
  });

  return Array.from(merged.values());
}

function toSequenceMap(taskSequences: readonly TaskSequence[]): ReadonlyMap<number, number> {
  const sequenceByTaskId = new Map<number, number>();
  taskSequences.forEach((task) => {
    if (task != null && task.id != null && task.sequence != null) {
      sequenceByTaskId.set(task.id, task.sequence);
    }
  });
  return sequenceByTaskId;
}

function toBrakeMeasurementMap(brakeMeasurements: readonly BrakeMeasurementOption[]): ReadonlyMap<number, string> {
  const measurementById = new Map<number, string>();
  brakeMeasurements.forEach((option) => {
    const measurement = option == null ? null : nonEmpty(option.measurement);
    if (option?.id != null && measurement !== null) {
      measurementById.set(option.id, measurement);
    }
  });
  return measurementById;
}

function compareCategories(a: MergedCategory, b: MergedCategory): number {
  return (
    compareNullableNumbers(a.categorySequence, b.categorySequence) ||
    (a.categoryName ?? '').localeCompare(b.categoryName ?? '') ||
    a.categoryId - b.categoryId
  );
}

function compareTasks(
  a: RawInspectionTask,
  b: RawInspectionTask,
  sequenceByTaskId: ReadonlyMap<number, number>
): number {
  return (
    compareNullableNumbers(sequenceByTaskId.get(a.taskId) ?? null, sequenceByTaskId.get(b.taskId) ?? null) ||
    a.taskId - b.taskId
  );
}

// Unknown ordering sorts last so admin-sequenced items keep their configured order.
function compareNullableNumbers(a: number | null, b: number | null): number {
  if (a == null && b == null) {
    return 0;
  }
  if (a == null) {
    return 1;
  }
  if (b == null) {
    return -1;
  }
  return a - b;
}

function toTaskRow(task: RawInspectionTask, measurementById: ReadonlyMap<number, string>): TaskRowViewModel {
  const status = toTaskStatus(task);
  return {
    taskId: task.taskId,
    label: buildTaskLabel(task, measurementById),
    status,
    measurements: buildMeasurements(task, status),
  };
}

// Every widget is chosen by the task's own display flag, so a renamed task, a new category or a
// drum brake prints the same way a factory task does.
function buildTaskLabel(task: RawInspectionTask, measurementById: ReadonlyMap<number, string>): string {
  const suffixes: string[] = [];

  if (task.displayBrakeMeasurement && task.brakeMeasurementId != null) {
    const measurement = measurementById.get(task.brakeMeasurementId);
    if (measurement !== undefined) {
      suffixes.push(` - ${measurement}`);
    }
  }
  if (task.displayTireSealantExpiration) {
    const date = toSealantExpirationDate(task.vehicleSealantExpirationDate);
    if (date !== null) {
      suffixes.push(`: ${date}`);
    }
  }
  if (task.displayFluidFill && task.fluidFilled) {
    suffixes.push(` ${FLUID_FILLED_SUFFIX}`);
  }

  return `${asText(task.taskName)}${suffixes.join('')}`;
}

function buildMeasurements(task: RawInspectionTask, status: TaskStatus): string[] {
  const measurements: string[] = [];
  const isInspected = status !== TaskStatus.Grey;

  if (task.displayOilLife) {
    measurements.push(isInspected && task.oilLife != null ? `${task.oilLife}%` : OIL_LIFE_NOT_AVAILABLE);
  }
  if (task.displayVoltage) {
    // 0 is the stored default for a voltage that was never entered, not a reading.
    const isMeasured = isInspected && task.batteryVoltage != null && task.batteryVoltage !== 0;
    measurements.push(isMeasured ? `${task.batteryVoltage}v` : VOLTAGE_NOT_MEASURED);
  }

  return measurements;
}

export function toSealantExpirationDate(value: string | null | undefined): string | null {
  const date = parseIsoCalendarDate(value);
  if (date === null || date.utc <= SEALANT_DATE_SENTINEL_LIMIT) {
    return null;
  }
  return `${date.year}-${date.month}-${date.day}`;
}

interface CalendarDate {
  readonly year: string;
  readonly month: string;
  readonly day: string;
  readonly utc: number;
}

// Dates arrive as ISO datetime text, zone-less ("2027-03-15T00:00:00") or at midnight UTC
// ("2027-03-15T00:00:00.000Z"); either way the stored calendar date is the text's own, so it is
// read from the text rather than shifted through the browser's time zone.
function parseIsoCalendarDate(value: string | null | undefined): CalendarDate | null {
  const match = ISO_DATE_PREFIX.exec(asText(value));
  if (match === null) {
    return null;
  }

  const [, year, month, day] = match;
  const utc = Date.UTC(Number(year), Number(month) - 1, Number(day));
  return Number.isNaN(utc) ? null : { year, month, day, utc };
}

// A task that was never assessed carries no box at all; the report treats it like Grey, the
// same way the inspection screen marks unselected tasks grey at save time.
export function toTaskStatus(task: RawInspectionTask): TaskStatus {
  if (task.redBox) {
    return TaskStatus.Red;
  }
  if (task.yellowBox) {
    return TaskStatus.Yellow;
  }
  if (task.greenBox) {
    return TaskStatus.Green;
  }
  return TaskStatus.Grey;
}

function buildHeader(
  header: VehicleInspectionReportHeader | null,
  inspectionName: string | null
): ReportHeaderViewModel {
  return {
    title: nonEmpty(header?.title) ?? nonEmpty(inspectionName) ?? DEFAULT_REPORT_TITLE,
    customerName: asText(header?.CustomerName),
    advisorName: asText(header?.AdvisorName),
    date: asText(header?.date),
    vehicle: [header?.VehicleYear, header?.VehicleMake, header?.VehicleModel]
      .map((part) => asText(part))
      .filter((part) => part.length > 0)
      .join(' '),
    workOrderId: asText(header?.WorkOrderId),
    tag: asText(header?.tag),
    vin: asText(header?.Vin),
  };
}

function nonEmpty(value: string | null | undefined): string | null {
  const text = asText(value);
  return text.length > 0 ? text : null;
}

function asText(value: string | number | null | undefined): string {
  if (value == null) {
    return '';
  }
  return String(value).trim();
}
